import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { build, buildWithParams, pool } from '@fitogest/reactive-form-builder/utils';
import {
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { BaseForm } from '../../../../base-form';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { RequestHandler } from '../../../../../service/OffService/request-handler';
import { TareaAlquilerMaquinariaModel } from '../../../../../models/tareas/tarea-alquiler-maquinaria.model';
import { TareasAlquilerMaquinariaFormDefinition } from './form-control/form-definition';

@Component({
    selector: 'app-tareas-alquiler-maquinaria-form',
    standalone: false,
    templateUrl: './tareas-alquiler-maquinaria-form.component.html',
    styleUrls: ['./tareas-alquiler-maquinaria-form.component.scss'],
})
export class TareasAlquilerMaquinariaFormComponent extends BaseForm<TareaAlquilerMaquinariaModel> implements OnInit {
    public form!: TareasAlquilerMaquinariaFormDefinition;
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public loadingData = true;
    public getRequest!: RequestHandler<TareaAlquilerMaquinariaModel[]>;
    public reactiveFormRecordId = '';
    public override model: TareaAlquilerMaquinariaModel = {};
    public initialPreviousData: TareaAlquilerMaquinariaModel | null = null;

    private lastReloadedClientDropdownId = '';
    private isReloadingClientDropdowns = false;

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public fincasApi: FincasApiService,
        public clientesApi: ClientesApiService,
        public trabajadoresApi: TrabajadoresApiService,
        public tareasApi: TareasApiService,
        public override router: Router,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.alquilerMaquinaria.PUT,
            tareasApi.alquilerMaquinaria.POST,
            'alquiler-maquinaria',
            'Actualizar Tarea Alquiler Maquinaria',
            'Realizar Tarea Alquiler Maquinaria',
            'Duplicar Tarea Alquiler Maquinaria',
        );
        this.getRequest = tareasApi.alquilerMaquinaria.GET;
    }

    public ngOnInit(): void {
        this.dashboard.clientChanges$.subscribe(() => {
            this.fincasApi.fincasGET.unsuscribe();
            
        });

        this.softInit(this.getType());
        void this.setupForm();
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<TareaAlquilerMaquinariaModel>): void {
        const stableValue = this.captureStableValueChange(event.value as TareaAlquilerMaquinariaModel);

        this.form.model = this.toDomainModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();

        if (event.origin !== 'user') {
            return;
        }

        this.handleUserValueChange(event.value, event.changedField);
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<TareaAlquilerMaquinariaModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            return;
        }

        const stableValue = this.captureStableValueChange(event.value as TareaAlquilerMaquinariaModel);

        this.form.model = this.toDomainModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();
        this.syncCalculatedHours(String(event.changedField ?? ''));
    }

    public onAfterSend(): void {
        StorageManager.saveSessionDataDonJardin(
            this.form.model.id_finca ?? '',
            this.form.model.hora_inicio ?? '',
            this.form.model.hora_fin?.toString() ?? '',
        );
    }

    private async setupForm(): Promise<void> {
        try {
            this.loadingData = true;
            this.isReactiveFormReady = false;
            this.reactiveFormLoadingError = '';
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId();
            const initialModel = this.initializeHostModel(this.toDomainModel(this.getModel() ?? {}));

            this.initialPreviousData = { ...initialModel };
            this.form = new TareasAlquilerMaquinariaFormDefinition(this.getType(), initialModel);
            this.form.model = this.toDomainModel(initialModel);

            await this.initFormData();
            this.syncModelFromForm();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.cdr.markForCheck();
            console.error('No se ha podido preparar el formulario de alquiler de maquinaria.', error);
        }
    }

    private async initFormData(): Promise<boolean> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        const buildsRequest = this.getDropdownBuildsForConfiguredCatalogs();

        return pool(buildsRequest, () => this.refreshReactiveDropdownMappings())
            .then(() => {
                this.loadingData = false;
                this.refreshReactiveDropdownMappings();

                return true;
            })
            .catch((error: unknown) => {
                this.loadingData = false;
                this.refreshReactiveDropdownMappings();
                console.error('Error loading MTO reactive form data.', error);
                throw error;
            });
    }

    private getDropdownBuildsForConfiguredCatalogs(): ReturnType<typeof build>[] {
        const configuredCatalogs = this.getConfiguredDropdownCatalogs();
        const buildsRequest: ReturnType<typeof build>[] = [];

        if (configuredCatalogs.has('id_finca')) {
            buildsRequest.push(build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre', true));
        }

        if (configuredCatalogs.has('id_cliente')) {
            buildsRequest.push(build(this.clientesApi.clientesGET, this.form.lists.clientes, { or: ['razon_social', 'nombre'] }));
        }

        if (configuredCatalogs.has('ids_trabajadores')) {
            buildsRequest.push(build(
                this.trabajadoresApi.trabajadoresGET,
                this.form.lists.trabajadores,
                (it: { nombre?: string; apellidos?: string }) => `${it.nombre ?? ''} ${it.apellidos ?? ''}`.trim(),
                true,
                true,
            ));
        }

        return buildsRequest;
    }

    private getConfiguredDropdownCatalogs(): Set<string> {
        const dropdownCatalogs = this.form.formFields.dropdownCatalogs;

        return new Set([
            ...(dropdownCatalogs?.critical ?? []),
            ...(dropdownCatalogs?.deferred ?? []),
            ...(dropdownCatalogs?.lazy ?? []),
        ]);
    }

    private handleUserValueChange(
        formModel: ReactiveFormValue<TareaAlquilerMaquinariaModel>,
        lastChangedFieldName: keyof TareaAlquilerMaquinariaModel | string | null | undefined,
    ): void {
        const changedFieldName = String(lastChangedFieldName ?? '');

        this.form.model = this.toDomainModel({
            ...this.form.model,
            ...formModel,
        });
        this.syncModelFromForm();
        this.syncCalculatedHours(changedFieldName);

        if (changedFieldName === 'mantenimiento') {
            this.form.updateMantenimientoLabel();
            this.refreshReactiveDropdownMappings();
        }

        if (changedFieldName === 'id_cliente') {
            this.onClientChanged();
        }

        if (changedFieldName === 'id_finca' || changedFieldName === 'id_sector') {
            this.refreshReactiveDropdownMappings();
        }
    }

    private syncCalculatedHours(changedFieldName: string): void {
        const updates = this.form.getHourUpdatesForChangedField(changedFieldName);
        const updateEntries = Object.entries(updates);

        if (updateEntries.length === 0) {
            return;
        }

        this.form.model = this.toDomainModel({
            ...this.form.model,
            ...updates,
        });
        this.syncModelFromForm();

        this.formBuilderRef?.updateForm(updates as Partial<TareaAlquilerMaquinariaModel>, true);
    }

    private onClientChanged(): void {
        const selectedClientId = this.getReactiveValueId(this.form.model.id_cliente);

        if (!selectedClientId || this.isReloadingClientDropdowns || selectedClientId === this.lastReloadedClientDropdownId) {
            return;
        }

        this.isReloadingClientDropdowns = true;
        this.lastReloadedClientDropdownId = selectedClientId;
        this.form.model = this.toDomainModel({
            ...this.form.model,
            id_finca: null,
        });
        this.syncModelFromForm();

        pool([
            buildWithParams(this.fincasApi.fincasGET, { id_cliente: selectedClientId }, this.form.lists.fincas, 'nombre', true),
            
        ], () => this.refreshReactiveDropdownMappings())
            .then(() => {
                this.form.lists.fincas.filtered = this.form.lists.fincas.values;
                this.isReloadingClientDropdowns = false;
                this.refreshReactiveDropdownMappings();

                return true;
            })
            .catch((error: unknown) => {
                this.isReloadingClientDropdowns = false;
                this.refreshReactiveDropdownMappings();
                console.error('Error reloading MTO client dropdowns.', error);
            });
    }

    private markReactiveFormReady(): void {
        this.loadingData = false;
        this.isReactiveFormReady = true;
        this.cdr.detectChanges();
        this.finalizeExternalDropdowns();
        this.refreshReactiveDropdownMappings();
        this.cdr.markForCheck();
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toDomainModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }

    private toDomainModel(
        sourceModel: Partial<TareaAlquilerMaquinariaModel> | ReactiveFormValue<TareaAlquilerMaquinariaModel>,
    ): TareaAlquilerMaquinariaModel {
        const normalizedModel: Record<string, unknown> = {
            ...(sourceModel as unknown as Record<string, unknown>),
        };

        return normalizedModel as unknown as TareaAlquilerMaquinariaModel;
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim().length > 0) {
            return error.message;
        }

        if (typeof error === 'string' && error.trim().length > 0) {
            return error;
        }

        return 'No se ha podido preparar el formulario de alquiler de maquinaria.';
    }

    private refreshReactiveDropdownMappings(): void {
        this.formBuilderRef?.requestExternalDropdownMappingsRefresh();
    }

    private getReactiveValueId(value: unknown): string {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            return String((value as { id?: unknown }).id ?? '');
        }

        return String(value ?? '');
    }
}
